import React, { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Header from "src/components/app/layout/Header";
import Layout from "src/components/app/layout/Layout";
import femaleAvatarIcon from "src/assets/images/icons/female_avatar.png";
import maleAvatarIcon from "src/assets/images/icons/male_avatar.png";
import doctorInfoIcon from "src/assets/images/icons/doctor_info.png";
import hospitalBuildingIcon from "src/assets/images/icons/doctor_building.png";
import emergencyContactIcon from "src/assets/images/icons/emergency_contact.png";
import { useAppSelector } from "src/redux/hooks";
import Button from "src/components/app/Button";
import { LucideMapPin } from "lucide-react";
import analytics from "src/helpers/analytics";
import auth from "src/helpers/auth";
import { SCREEN_NAMES } from "src/helpers/analytics/events";
import environment from "src/constants/environment";
import storage from "src/helpers/storage";
import { genderOptions, STORAGE_KEYS } from "src/constants/common";

const ProfileScreen: React.FC = () => {
  const params = useParams();
  const supportProgramKey = params["support-program-key"];
  const navigate = useNavigate();
  const patient = useAppSelector((state) => state.auth.patient.data);

  const appVersion = storage.getItem(STORAGE_KEYS.APP_VERSION) || "1.0.0";
  const buildNumber = storage.getItem(STORAGE_KEYS.BUILD_NUMBER) || "1";
  const env = environment.ENV;

  useEffect(() => {
    // page view
    analytics.pageView(SCREEN_NAMES.PROFILE_SCREEN);
  }, []);

  // Format height from decimal to feet and inches
  const formatHeight = (
    height: number | string | undefined,
    unit: string | undefined
  ) => {
    if (!height) return "N/A";

    const heightNum = typeof height === "string" ? parseFloat(height) : height;

    if (unit === "feet_inch") {
      const feet = Math.floor(heightNum);
      const inches = Math.round((heightNum - feet) * 100);
      return `${feet}' ${inches}"`;
    }

    return `${heightNum} ${unit || ""}`;
  };

  // Format weight
  const formatWeight = (
    weight: number | string | undefined,
    unit: string | undefined
  ) => {
    if (!weight) return "N/A";

    const weightNum = typeof weight === "string" ? parseFloat(weight) : weight;
    return `${weightNum} ${unit || "kg"}`;
  };

  const handleLogout = async () => {
    await auth.handleLogout();
  };

  return (
    <Layout
      supportProgramKey={supportProgramKey || ""}
      header={
        <Header
          title="My Profile"
          titlePosition="center"
          showBackButton={true}
          onBackClick={() => {
            navigate(`/app/${supportProgramKey}/home-screen`, {
              replace: true,
            });
          }}
        />
      }
      stickyHeader={true}
    >
      <div className="relative w-full px-4 py-4">
        <div className="flex flex-col gap-y-5">
          <div className="relative w-full space-y-2">
            <div className="relative bg-white rounded-lg p-4 border border-gray-200">
              <div className="flex items-center gap-x-3">
                <img
                  src={
                    patient?.gender === "M" ? maleAvatarIcon : femaleAvatarIcon
                  }
                  className="w-12 h-12 rounded-full"
                />
                <div className="flex flex-col">
                  <h6 className="text-base font-bold capitalize">
                    {patient?.name}
                  </h6>
                  <p className="text-sm text-gray-500">
                    {patient?.country_code} {patient?.contact_no}
                  </p>
                </div>
              </div>
              <div className="pt-4 mt-4 border-t border-gray-200">
                <div className="flex flex-col gap-y-2">
                  <div className="flex items-center justify-between gap-x-2">
                    <h6 className="text-sm font-semibold capitalize">
                      Gender:
                    </h6>
                    <p className="text-sm text-gray-500">
                      {genderOptions.find(
                        (gender) => gender.value === patient?.gender
                      )?.label || "N/A"}
                    </p>
                  </div>
                  <div className="flex items-center justify-between gap-x-2">
                    <h6 className="text-sm font-semibold capitalize">DOB:</h6>
                    <p className="text-sm text-gray-500">
                      {patient?.dob
                        ? new Date(patient?.dob).toLocaleDateString()
                        : "N/A"}
                    </p>
                  </div>
                  <div className="flex items-center justify-between gap-x-2">
                    <h6 className="text-sm font-semibold capitalize">
                      Height:
                    </h6>
                    <p className="text-sm text-gray-500">
                      {formatHeight(patient?.height, patient?.height_unit)}
                    </p>
                  </div>
                  <div className="flex items-center justify-between gap-x-2">
                    <h6 className="text-sm font-semibold capitalize">
                      Weight:
                    </h6>
                    <p className="text-sm text-gray-500">
                      {formatWeight(patient?.weight, patient?.weight_unit)}
                    </p>
                  </div>
                  <div className="flex items-center justify-between gap-x-2">
                    <h6 className="text-sm font-semibold capitalize">Email:</h6>
                    <p className="text-sm text-gray-500">
                      {patient?.email || "N/A"}
                    </p>
                  </div>
                </div>
              </div>
              <div className="pt-4 mt-4">
                <Button
                  variant="contained"
                  rounded="full"
                  fullWidth
                  onClick={() => {
                    navigate(`/app/${supportProgramKey}/profile-screen/edit`);
                  }}
                >
                  Complete Profile
                </Button>
              </div>
            </div>
          </div>
          <div className="relative w-full space-y-2">
            <h2 className="text-lg font-bold">Emergency Contact/Caregiver</h2>
            <div className="relative bg-white rounded-lg p-4 border border-gray-200">
              {patient?.patient_meta_info?.cg_name ? (
                <>
                  <div className="flex items-center gap-x-3">
                    <img
                      src={emergencyContactIcon}
                      className="w-12 h-12 rounded-full"
                    />
                    <div className="w-full flex flex-col">
                      <h6 className="text-base font-bold capitalize">
                        {patient?.patient_meta_info?.cg_name}
                      </h6>
                      <p className="text-sm text-gray-500">
                        {patient?.patient_meta_info?.cg_contact_no
                          ? `+91 ${patient?.patient_meta_info?.cg_contact_no}`
                          : "N/A"}
                      </p>
                    </div>
                  </div>
                  {patient?.patient_meta_info?.cg_relation && (
                    <div className="pt-4 mt-4 border-t border-gray-200">
                      <div className="flex items-center justify-between gap-x-2">
                        <h6 className="text-sm font-semibold capitalize">
                          Relation:
                        </h6>
                        <p className="text-sm text-gray-500 capitalize">
                          {patient?.patient_meta_info?.cg_relation}
                        </p>
                      </div>
                    </div>
                  )}
                  <div className="pt-4 mt-4">
                    <Button
                      variant="contained"
                      rounded="full"
                      fullWidth
                      onClick={() => {
                        navigate(
                          `/app/${supportProgramKey}/profile-screen/add-care-giver`
                        );
                      }}
                    >
                      Edit Details
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  <div className="flex items-center justify-center">
                    <img
                      src={emergencyContactIcon}
                      className="w-12 h-12 rounded-full"
                    />
                    {/* <div className="w-full flex flex-col">
                      <div className="flex flex-col gap-y-1">
                        <div className="w-[100px] h-4 bg-yellow-200/50 rounded-full" />
                        <div className="w-[150px] h-4 bg-yellow-200/50 rounded-full" />
                      </div>
                    </div> */}
                  </div>
                  <div className="flex flex-col gap-y-4 mt-4">
                    <p className="text-sm text-gray-500 text-center">
                      Caregiver is an individual who provides, physical,
                      emotional, and practical support to the patient.
                    </p>
                    <Button
                      variant="contained"
                      rounded="full"
                      fullWidth
                      onClick={() => {
                        navigate(
                          `/app/${supportProgramKey}/profile-screen/add-care-giver`
                        );
                      }}
                    >
                      Add Caregiver
                    </Button>
                  </div>
                </>
              )}
            </div>
          </div>
          <div className="relative w-full space-y-2">
            <h2 className="text-lg font-bold">Doctor Details</h2>
            <div className="relative bg-white rounded-lg p-4 border border-gray-200 overflow-hidden">
              {/* <div className="absolute top-1/2 -translate-y-1/2 left-[-14%]">
                <img
                  src={hospitalBuildingIcon}
                  className="w-28 h-full rounded-full"
                />
              </div> */}
              <div className="flex items-center gap-x-3">
                <img src={doctorInfoIcon} className="w-12 h-12 rounded-full" />
                <div className="w-fullflex flex-col">
                  <h6 className="text-base font-bold capitalize">
                    {patient?.doctor_details?.doctor_name || "N/A"}
                  </h6>
                  <p className="text-sm text-gray-500">
                    {patient?.doctor_details?.doctor_phone}
                  </p>
                </div>
              </div>
              {patient?.patient_meta_info?.hospital && (
                <div className="flex flex-col gap-y-2 mt-4">
                  <div className="flex items-start gap-x-2">
                    <span className="mt-1">
                      <LucideMapPin className="w-5 h-5" />
                    </span>
                    <div>
                      <p className="text-base font-bold">
                        {patient?.patient_meta_info?.hospital}
                      </p>
                    </div>
                  </div>
                </div>
              )}
              {/* <div className="flex flex-col gap-y-1">
                  <div className="w-[100px] h-4 bg-pink-200/50 rounded-full" />
                  <div className="w-[150px] h-4 bg-pink-200/50 rounded-full" />
                </div> */}
            </div>
          </div>

          <div className="flex items-center justify-center">
            <p className="text-sm text-gray-500">{`App Version: ${appVersion}(${buildNumber})${
              env !== "prod" ? `(${env})` : ""
            }`}</p>
          </div>

          <div className="relative w-full text-center">
            <Button
              color="danger"
              // className="px-2 py-0.5 text-base font-bold text-red-500"
              onClick={handleLogout}
              fullWidth
              rounded="full"
            >
              Log Out
            </Button>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default ProfileScreen;
